import React, { useState, useEffect } from 'react';
import { Container, Button, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';

const HelpCenter = () => {
  const navigate = useNavigate();
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const faqs = [
    {
      q: 'Why does the converted amount of assets change?',
      a: 'The conversion in assets is the value of the current digital currency held in USDT, which changes due to price fluctuations of digital assets. The amount of your digital assets has not changed.',
    },
    {
      q: 'What to do if you forget your fund password for platform transactions?',
      a: 'Go to "My—Settings—Click to set fund password" or contact customer service to reset it.',
    },
    {
      q: 'How to calculate the account equity of user contracts?',
      a: 'Account equity = position margin + position floating profit and loss + current account available amount',
    },
    {
      q: 'What is the numerical function of stop loss and take profit set when opening a position? How should it be set?',
      a: 'They serve as profit and loss limits. Set appropriate levels based on your situation to avoid losses.',
    },
    {
      q: 'How to reduce contract risk?',
      a: 'Transfer assets from other accounts or reduce position to reduce risk.',
    },
    {
      q: 'What does the quantity in a contract opening represent?',
      a: 'It indicates the number of coins you expect to buy. Eg: 10 BTC at 1000USDT each = quantity 10.',
    },
    {
      q: 'How are the handling fees in the contract calculated?',
      a: 'Handling fee = opening price * quantity * handling fee rate.',
    },
    {
      q: 'Instructions on forced liquidation',
      a: 'Liquidation occurs when your risk degree reaches 100%. The system may close positions to prevent further losses.',
    },
    {
      q: 'What are the contract trading rules?',
      a: 'Trading types: Long (buying), Short (selling). Types of orders include market and limit orders.',
    },
    {
      q: 'What is margin in contract trading?',
      a: 'Funds needed as guarantee. Margin = opening price * quantity / leverage multiple.',
    },
    {
      q: 'What are limit orders and market orders?',
      a: 'Limit orders specify price; market orders execute at current market price.',
    },
    {
      q: 'What does the risk level of contract trading represent?',
      a: 'It indicates your risk. 100% = liquidation. Above 50% means use caution.',
    },
    {
      q: 'Why do you need currency conversion?',
      a: 'Allows smooth circulation of funds. USDT can be freely converted into QCC for trading.',
    },
    {
      q: 'How to buy Tether (USDT) through the platform?',
      a: 'Use the platform’s buy/sell list, or post an order. Orders not paid in 15 mins are auto-cancelled.',
    },
  ];

  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        minHeight: '100vh',
      }}
    >
      {/* Header with full-width background */}
      <div
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          width: '100%',
        }}
      >
        <Container
          fluid
          style={{
            maxWidth: isMobile ? '100%' : '85%',
            margin: '0 auto',
            padding: isMobile ? '0' : '0 1rem',
          }}
        >
          <div className='d-flex justify-content-between align-items-center p-3'>
            <Button
              variant='link'
              onClick={() => navigate(-1)}
              className='p-0'
              aria-label='Go back'
              style={{ color: '#FFFFFF' }}
            >
              <ArrowLeft size={24} />
            </Button>
            <h5 className='mb-0 fw-bold' style={{ color: '#FFFFFF' }}>
              Help Center
            </h5>
            <div style={{ width: '24px' }}></div>
          </div>
        </Container>
      </div>

      {/* Main Content with constrained width */}
      <Container
        fluid
        style={{
          maxWidth: isMobile ? '100%' : '85%',
          padding: 'clamp(1rem, 3vw, 1.5rem)',
          backgroundColor: '#FFFFFF',
          minHeight: '100vh',
          margin: '0 auto',
        }}
      >
        <div style={{ paddingBottom: '100px' }}>
          {/* FAQ Section */}
          <div className='mb-4'>
            <div className='d-flex flex-column gap-3'>
              {faqs.map((item, idx) => (
                <Card key={idx} className='border-0 shadow-sm'>
                  <Card.Body className='p-0'>
                    <div
                      className='d-flex justify-content-between align-items-center p-3'
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        setOpenFaqIndex(openFaqIndex === idx ? null : idx)
                      }
                    >
                      <h6
                        className='fw-semibold mb-0'
                        style={{ fontSize: '0.9rem' }}
                      >
                        {item.q}
                      </h6>
                      {openFaqIndex === idx ? (
                        <ChevronUp
                          size={20}
                          style={{ color: '#667eea', flexShrink: 0 }}
                        />
                      ) : (
                        <ChevronDown
                          size={20}
                          style={{ color: '#667eea', flexShrink: 0 }}
                        />
                      )}
                    </div>
                    {openFaqIndex === idx && (
                      <div
                        className='px-3 pb-3'
                        style={{
                          borderTop: '1px solid #f0f0f0',
                          paddingTop: '12px',
                          animation: 'fadeIn 0.3s ease-in-out',
                        }}
                      >
                        <p className='text-muted small mb-0'>{item.a}</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </Container>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 576px) {
          h5 {
            font-size: 1.1rem !important;
          }
          h6 {
            font-size: 1rem !important;
          }
        }

        @media (max-width: 380px) {
          h5 {
            font-size: 1rem !important;
          }
          h6 {
            font-size: 0.95rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default HelpCenter;
